import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { IFilterModel, ILeaseStatus, ISortModel } from '@/types/admin/residentList';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { SearchBar } from '@/components/common/admin/mobile/SearchBar';

interface ResidentListFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterModel: IFilterModel;
  onFilterChange: (filterModel: IFilterModel) => void;
  sortModel: ISortModel[];
  onSortChange: (sortModel: ISortModel[]) => void;
}

const LEASE_STATUS_OPTIONS = [
  { value: 'current', label: 'Current' },
  { value: 'notice', label: 'Notice' },
  { value: 'future', label: 'Future' },
  { value: 'past', label: 'Past' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'lease_status', label: 'Lease Status' },
  { field: 'unit', label: 'Unit' },
  { field: 'customer_type', label: 'Customer Type' },
];

export const ResidentListFilters: React.FC<ResidentListFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filterModel,
  onFilterChange,
  sortModel,
  onSortChange,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  // Get currently selected lease statuses
  const selectedLeaseStatuses = filterModel.items
    .filter(item => item.field === 'lease_status')
    .map(item => item.value);

  // Handle lease status filter toggle
  const handleLeaseStatusToggle = (status: string) => {
    const currentStatuses = selectedLeaseStatuses;
    let newStatuses: string[];

    if (currentStatuses.includes(status)) {
      newStatuses = currentStatuses.filter(s => s !== status);
    } else {
      newStatuses = [...currentStatuses, status];
    }

    // Update filter model
    const newFilterModel: IFilterModel = {
      items: [
        ...filterModel.items.filter(item => item.field !== 'lease_status'),
        ...newStatuses.map(statusValue => ({
          field: 'lease_status',
          operator: 'is',
          value: statusValue,
        })),
      ],
    };

    onFilterChange(newFilterModel);
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    const currentSort = sortModel[0];
    let newSort: ISortModel;

    if (currentSort?.field === field) {
      // Toggle sort direction
      newSort = {
        field,
        sort: currentSort.sort === 'asc' ? 'desc' : 'asc',
      };
    } else {
      // New field, default to ascending
      newSort = { field, sort: 'asc' };
    }

    onSortChange([newSort]);
  };

  const getSortIcon = (field: string): string => {
    const currentSort = sortModel[0];
    if (currentSort?.field === field) {
      return currentSort.sort === 'asc' ? '↑' : '↓';
    }
    return '';
  };

  return (
    <View style={styles.container}>
      {/* Search Bar */}
      <SearchBar
        placeholder="Search residents by name..."
        value={searchQuery}
        onChangeText={onSearchChange}
        style={styles.searchBar}
      />

      {/* Filter Toggle */}
      <TouchableOpacity
        style={styles.filterToggle}
        onPress={() => setShowFilters(!showFilters)}
      >
        <View style={styles.filterToggleContent}>
          <Text style={styles.filterToggleText}>
            Filters
          </Text>
          <SVG_COMPONENTS.FILTER width={16} height={16} />
        </View>
      </TouchableOpacity>

      {/* Filters Panel */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          {/* Lease Status Filters */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Lease Status</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.filterChipsContainer}>
                {LEASE_STATUS_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.filterChip,
                      selectedLeaseStatuses.includes(option.value) && styles.filterChipSelected,
                    ]}
                    onPress={() => handleLeaseStatusToggle(option.value)}
                  >
                    <Text
                      style={[
                        styles.filterChipText,
                        selectedLeaseStatuses.includes(option.value) && styles.filterChipTextSelected,
                      ]}
                    >
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>

          {/* Sort Options */}
          <View style={styles.filterSection}>
            <Text style={styles.filterSectionTitle}>Sort By</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <View style={styles.filterChipsContainer}>
                {SORT_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.field}
                    style={[
                      styles.sortChip,
                      sortModel[0]?.field === option.field && styles.sortChipSelected,
                    ]}
                    onPress={() => handleSortChange(option.field)}
                  >
                    <Text
                      style={[
                        styles.sortChipText,
                        sortModel[0]?.field === option.field && styles.sortChipTextSelected,
                      ]}
                    >
                      {option.label} {getSortIcon(option.field)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.HEADER_BACKGROUND,
    borderBottomWidth: 2,
    borderBottomColor: ADMIN_COLORS.DATA_TABLE.BORDER,
    width: '100%', // Ensure full width
    zIndex: 1, // Ensure it stays above other content
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  searchBar: {
    marginHorizontal: 16,
    marginVertical: 12,
  },
  filterToggle: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.DATA_TABLE.BORDER,
  },
  filterToggleContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  filterToggleText: {
    fontSize: 14,
    color: ADMIN_COLORS.DATA_TABLE.TEXT,
    fontWeight: 'bold',
  },
  filtersPanel: {
    paddingHorizontal: 16,
    paddingTop: 10,
    backgroundColor: ADMIN_COLORS.DATA_TABLE.BACKGROUND,
  },
  filterSection: {
    marginBottom: 16,
  },
  filterSectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#757575',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  filterChipsContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#BDBDBD',
    backgroundColor: '#FFFFFF',
  },
  filterChipSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  filterChipText: {
    fontSize: 12,
    color: '#212121',
  },
  filterChipTextSelected: {
    color: '#FFFFFF',
  },
  sortChip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    backgroundColor: '#FFFFFF',
  },
  sortChipSelected: {
    backgroundColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
    borderColor: ADMIN_COLORS.DATA_TABLE.FILTER_BACKGROUND,
  },
  sortChipText: {
    fontSize: 12,
    color: '#212121',
  },
  sortChipTextSelected: {
    color: '#FFFFFF',
  },
});
